import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../../common/Modal';
import { useProfileStore } from '../../../state/profileStore';
import { useSignup } from './SignupContext';  // Fixed the capitalization
import {
  isCompetitorDomain,
  isPublicEmailDomain,
  isBigCompanyDomain,
  BLOCK_MESSAGE,
  PUBLIC_EMAIL_MESSAGE,
  SMALL_BIZ_ONLY_MESSAGE,
} from '../../../utils/competitorBlocklist';
import { detectSignupUrl } from '../../../utils/signupUrlDetector';

const SignupModal = ({ isOpen, onClose }) => {  // remove onLoginClick from props
  const navigate = useNavigate();
  const { login } = useProfileStore();
  const { handleLoginClick } = useSignup();  // add this line with your other hooks
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Add missing state variables
  const [verificationToken, setVerificationToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [locationGuess, setLocationGuess] = useState('');
  // Phase 0+1: smart-URL detection drives the form layout. Detector runs
  // on URL-field blur. detection drives instruction text + which fields
  // and buttons render; toast is the transient "Detected: X" notification.
  const [detection, setDetection] = useState(null);
  const [toast, setToast] = useState(null);
  // Phase 3a/3b: stub message shown under the OAuth button when clicked,
  // until the real OAuth flow lands in Phase 3c-3e.
  const [oauthStubMsg, setOauthStubMsg] = useState('');

  // Toast lifecycle: visible 3s, fade 500ms, then remove from DOM. X button
  // clears immediately. Re-running setToast({message, fading:false}) resets
  // the cycle (new detection while a toast is still on screen).
  useEffect(() => {
    if (!toast) return;
    if (toast.fading) {
      const t = setTimeout(() => setToast(null), 500);
      return () => clearTimeout(t);
    }
    const t = setTimeout(
      () => setToast(prev => (prev ? { ...prev, fading: true } : null)),
      3000
    );
    return () => clearTimeout(t);
  }, [toast]);

  // Patterns that trigger an OAuth-button morph instead of email+submit.
  // FB/IG = Phase 3c/3d (shipped); TikTok = Phase 4 (still stubbed).
  // LinkedIn deliberately NOT here — we don't have LinkedIn Pages
  // approval, only personal-page posting, so we can't verify Company
  // Page ownership at signup time. Folded into Phase 6 (long-tail
  // email-only verification) for later.
  const OAUTH_PATTERNS = ['facebook', 'instagram', 'tiktok'];
  const isPersonalProfile = !!detection?.isPersonal;
  const isUnsupportedPlatform = detection?.pattern === 'linkedin';
  const isOAuthFlow   = !!(detection && OAUTH_PATTERNS.includes(detection.pattern)) && !isPersonalProfile;
  const showEmailField = !!detection && !isOAuthFlow && !isPersonalProfile && !isUnsupportedPlatform;
  const platformLabel = detection?.pattern === 'facebook'  ? 'Facebook'
                      : detection?.pattern === 'instagram' ? 'Instagram'
                      : detection?.pattern === 'tiktok'    ? 'TikTok' : '';

  // Per-detection instruction copy (matches the spec in
  // project_signup_smart_url_detection.md).
  function instructionFor(d) {
    if (!d) {
      return 'eHandshake is a business to business platform. Enter your business URL — your website, a Facebook or Instagram page, a TikTok profile, a Google Business Profile, or anywhere your business lives online.';
    }
    // Personal-profile rejection takes priority over the platform copy.
    if (d.isPersonal) {
      if (d.pattern === 'facebook')  return "That looks like a personal Facebook profile. Use your Business Page URL instead (e.g., facebook.com/yourbiz).";
      if (d.pattern === 'linkedin')  return "LinkedIn personal profiles aren't supported. Please use your Company Page URL (e.g., linkedin.com/company/yourbiz).";
      return "That looks like a personal profile, not a business page.";
    }
    switch (d.pattern) {
      case 'website':   return `Your email must match your website's domain (e.g., name@${d.bareDomain}).`;
      case 'franchise': return `Use your business email matching the parent domain (${d.bareDomain}).`;
      case 'facebook':  return "We'll verify ownership using your Facebook login. Must be a Business Page — personal profiles aren't supported.";
      case 'instagram': return "Your Instagram must be a Business or Creator account. (Free to switch in the IG app: Settings → Account type.)";
      case 'linkedin':  return "LinkedIn sign-up isn't available yet. Please paste your website URL, a Facebook page, an Instagram profile, or another business URL instead.";
      case 'tiktok':    return "Your TikTok must be a Business account. (Free to switch in Settings → Account.)";
      case 'gbp':       return "We'll confirm your Google Business Profile listing exists, then send a verification link to the email you enter.";
      case 'unknown':   return "We don't recognize this URL pattern, but you can still try with a business email.";
      default:          return "We'll send a verification link to the email you enter — that's how we confirm you can receive mail for this business.";
    }
  }
  const instructionText = instructionFor(detection);

  function handleUrlBlur(e) {
    const v = (e.target.value || '').trim();
    if (!v) {
      setDetection(null);
      setToast(null);
      return;
    }
    const d = detectSignupUrl(v);
    setDetection(d);
    setToast({
      message: d.pattern === 'unknown'
        ? "We don't recognize this URL — you can still try it"
        : `Detected: ${d.label}`,
      fading: false,
    });
    setOauthStubMsg('');
  }

  function handleOAuthStub() {
    setOauthStubMsg(`${platformLabel} sign-in is being wired up — coming soon. For now, if you have a regular business website you can use that instead.`);
  }

  // OAuth signup popup launcher — shared by all platforms. Opens
  // /api/auth/signup/oauth/<platform>/start, which redirects to the
  // platform's OAuth, which redirects back to /<platform>/callback. The
  // popup postMessages a 'signup-oauth-callback' to this window with
  // either { ok: true, redirectTo } or { ok: false, error }. The redirectTo
  // path is the email-collect page that completes the magic-link flow.
  function handleOAuthSignup(platform) {
    setError('');
    setOauthStubMsg('');
    const popupUrl = `/api/auth/signup/oauth/${platform}/start?url=${encodeURIComponent(website.trim())}`;
    const popup = window.open(popupUrl, `${platform}-signup-popup`, 'width=620,height=720');
    if (!popup) {
      setError('Pop-up was blocked — please allow popups for this site and try again.');
      return;
    }

    const handler = (event) => {
      if (event.data?.type !== 'signup-oauth-callback') return;
      window.removeEventListener('message', handler);
      const data = event.data.data || {};
      if (data.ok) {
        if (data.redirectTo) {
          onClose();
          window.location.href = data.redirectTo;
        } else {
          // Stub / dev-only response — no redirect. Surface inline for
          // visual verification of the OAuth roundtrip.
          setOauthStubMsg(`✅ ${data.message || 'OAuth roundtrip OK'}`);
        }
      } else {
        setError(data.error || `${platformLabel || 'Social'} sign-up failed.`);
      }
    };
    window.addEventListener('message', handler);
  }

  // Route the OAuth button click to the platform-specific handler.
  // Phase 3c wires Facebook; Phase 3d wires Instagram; Phase 4 wires
  // TikTok. LinkedIn was dropped (no Pages access).
  function handleOAuthClick() {
    if (
      detection?.pattern === 'facebook'
      || detection?.pattern === 'instagram'
      || detection?.pattern === 'tiktok'
    ) {
      handleOAuthSignup(detection.pattern);
    } else {
      handleOAuthStub();
    }
  }

  /*
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setVerificationToken(token);
      setIsVerifying(true);
      setSuccessMessage(''); // Clear any existing messages
    }


    // Check if we're in the main window and need to redirect
    const shouldRedirect = sessionStorage.getItem('verificationComplete');
    if (shouldRedirect) {
      sessionStorage.removeItem('verificationComplete');
      window.location.replace('/profile');
    }

    // Add event listener for verification success
    const handleVerificationSuccess = () => {
      onClose(); // Close the modal
      window.location.replace('/profile'); // Redirect in the main window
    };
    
    window.addEventListener('verificationSuccess', handleVerificationSuccess);
    return () => window.removeEventListener('verificationSuccess', handleVerificationSuccess);
  }, [onClose]);
  */

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    console.log('1. Starting password submission...');

    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      console.log('2. Token:', verificationToken);
      const verifyResponse = await fetch('/api/auth/complete-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token: verificationToken, 
          password
        }),
        credentials: 'include'
      });

      const verifyData = await verifyResponse.json();
      console.log('3. Verify response:', verifyData, 'Status:', verifyResponse.status);

      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Verification failed');
      }

      console.log('4. Attempting login with:', verifyData.u_email);
      const loginResult = await login(verifyData.u_email, password);
      console.log('5. Login result:', loginResult, 'User data:', loginResult?.user);

      if (!loginResult || !loginResult.success) {
        throw new Error('Login failed - ' + (loginResult?.error || 'unknown error'));
      }

      console.log('6. Login successful, waiting before navigation...');
      // Add a small delay to see the state transition
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('7. Closing modal and navigating...');
      onClose();
      window.location.href = '/profile';

    } catch (error) {
      console.error('Password submission error:', error);
      setError(error.message || 'An error occurred during verification');
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Pressing Enter in the URL field fires form.onSubmit even when the
    // OAuth button has replaced the Sign Up button. Intercept so social
    // URLs trigger the OAuth path (opens popup for FB; stub for IG/LI/TT)
    // instead of the email-validation path below.
    if (isOAuthFlow) {
      handleOAuthClick();
      return;
    }

    // Belt-and-suspenders against Enter-in-URL before the email field is
    // filled: the email input has `required` so the browser usually catches
    // this, but if it doesn't we'd crash on email.split('@')[1].toLowerCase().
    if (!email || !email.includes('@')) {
      return;
    }

    setIsLoading(true);

    try {
      // Normalize website: lowercase, strip protocol/www, query params, trailing slashes
      const cleanWebsite = website
        .trim()
        .replace(/^(https?:\/\/)?(www\.)?/, '')
        .replace(/\?.*$/, '')   // strip query params (UTM tracking, etc.)
        .replace(/\/+$/, '')    // strip trailing slashes
        .toLowerCase();

      const emailDomain = email
        .split('@')[1]
        .toLowerCase();

      // ── Public/personal email providers ──
      // Mirrored on the server in /api/auth/signup so curl can't bypass.
      if (isPublicEmailDomain(email) || isPublicEmailDomain(cleanWebsite)) {
        setError(PUBLIC_EMAIL_MESSAGE);
        setIsLoading(false);
        return;
      }

      // ── Big company blocklist ──
      // Refuse signups from large tech/SaaS/platform companies that could
      // replicate the system if they understood it.
      if (isBigCompanyDomain(email) || isBigCompanyDomain(cleanWebsite)) {
        setError(SMALL_BIZ_ONLY_MESSAGE);
        setIsLoading(false);
        return;
      }

      // ── Competitor blocklist (client-side UX gate — server enforces too) ──
      // Check both the email domain and the website against our shared
      // blocklist so we surface the message BEFORE hitting the API.
      if (isCompetitorDomain(email) || isCompetitorDomain(cleanWebsite)) {
        setError(BLOCK_MESSAGE);
        setIsLoading(false);
        return;
      }

      // ── Domain match validation (re-enabled) ──
      // Strip path for franchise URLs before comparing, e.g.
      //   email: name@mybusiness.com, website: mybusiness.com/orlando
      //   → mybusiness.com === mybusiness.com ✓
      const websiteDomain = cleanWebsite.split('/')[0];
      if (emailDomain !== websiteDomain) {
        setError('Your email domain must match your website domain.');
        setIsLoading(false);
        return;
      }

      // For display/storage, ensure www. is present
      const displayWebsite = 'www.' + cleanWebsite;

      // Create both user and business in one request
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          u_email: email,
          b_website: displayWebsite,
          b_name: cleanWebsite,
          b_domain: cleanWebsite,
          u_signup_location: locationGuess
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Signup failed');
      }

      // Show success message immediately
      setSuccessMessage(`We have sent an email to ${email}. Please check your email for verification instructions.`);
      setEmail('');
      setWebsite('');
      setIsLoading(false);

      // AI analysis now runs server-side in the signup handler (no auth needed)
      // Send support notification
      fetch('/api/auth/notify-support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: email,
          websiteDomain: displayWebsite,
          timestamp: new Date().toISOString()
        }),
      }).catch(console.error);

      // Show success message for 5 seconds, then redirect
      setTimeout(() => {
        onClose();
        navigate('/closing');
      }, 5000);

    } catch (error) {
      setError(error.message);
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (isVerifying) {
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-center">Set Your Password</h2>
          {error && <div className="text-red-500 text-center">{error}</div>}
          {successMessage && <div className="text-green-500 text-center">{successMessage}</div>}
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setError('');

    try {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      const loginResult = await login(email, password);
      if (!loginResult || !loginResult.success) {
        setError('Login failed. Please try again.');
        return;
      }

      setTimeout(() => {
        window.location.href = '/profile';
      }, 1000);
    } catch (error) {
      console.error('Error during login:', error);
      setError('Unexpected error during login');
    }
  }}
  className="space-y-4"
>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-2 text-gray-500"
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-2 text-gray-500"
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            <button
              type="submit"
              className="w-full bg-brand text-white p-2 rounded hover:bg-brand-dark transition-colors relative"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">Complete Signup</span>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </>
              ) : (
                'Complete Signup'
              )}
            </button>
          </form>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-bold text-center text-[#00aef7]">Sign Up for eHandshake</h2>

        {successMessage ? (
          <div className="text-green-500 text-center p-4">{successMessage}</div>
        ) : (
          <>
            {/* Single dynamic instruction line — morphs with detection. */}
            <p className="text-sm text-gray-700 text-center px-1">
              {instructionText}
            </p>
            {!detection && (
              <p className="text-xs text-gray-400 text-center -mt-2">
                e.g., mybusiness.com · facebook.com/mybusiness · tiktok.com/@mybusiness
              </p>
            )}

            {error && <div className="text-red-500 text-center text-sm">{error}</div>}

            <form onSubmit={handleSignup} className="space-y-4">
              {/* URL field — always shown. Toast appears under it on blur. */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter your Business URL"
                  value={website}
                  onChange={(e) => {
                    setWebsite(e.target.value);
                    if (detection) setDetection(null);  // clear stale detection while editing
                    setOauthStubMsg('');
                  }}
                  onBlur={handleUrlBlur}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
                  autoComplete="url"
                  required
                />
                {toast && (
                  <div
                    className={`absolute left-0 right-0 top-full mt-1 z-20 pointer-events-none transition-opacity duration-500 ${toast.fading ? 'opacity-0' : 'opacity-100'}`}
                  >
                    <div className="bg-gray-900 text-white text-xs rounded-md shadow-lg px-3 py-2 flex items-center justify-between">
                      <span>{toast.message}</span>
                      <button
                        type="button"
                        onClick={() => setToast(null)}
                        className="ml-3 text-gray-400 hover:text-white text-base leading-none pointer-events-auto"
                        aria-label="Dismiss"
                      >×</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Email field — appears after detection for non-OAuth flows. */}
              {showEmailField && (
                <input
                  type="email"
                  placeholder="Business Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
                  required
                  autoFocus
                />
              )}

              {/* Personal-profile rejection AND unsupported-platform
                  rejection (LinkedIn until Phase 6) — no button, the
                  instruction text above already tells the user what
                  alternative URL to paste. */}
              {(isPersonalProfile || isUnsupportedPlatform) ? null : isOAuthFlow ? (
                <div>
                  <button
                    type="button"
                    onClick={handleOAuthClick}
                    className="w-full bg-brand text-white p-2 rounded hover:bg-brand-dark transition-colors"
                  >
                    Sign in with {platformLabel}
                  </button>
                  {oauthStubMsg && (
                    <p className="text-xs text-gray-500 italic mt-2 text-center">{oauthStubMsg}</p>
                  )}
                </div>
              ) : (
                <button
                  type="submit"
                  className="w-full bg-brand text-white p-2 rounded hover:bg-brand-dark transition-colors relative"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="opacity-0">Sign Up</span>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </>
                  ) : (
                    'Sign Up'
                  )}
                </button>
              )}
            </form>
          </>
        )}

        {!successMessage && (
          <>
            <div className="text-sm text-gray-600 text-center">
              By signing up, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark">Privacy Policy</a>
            </div>
            <div className="text-sm text-gray-600 text-center">
              Already have an account?{' '}
              <button
                onClick={() => {
                  onClose();
                  handleLoginClick();
                }}
                className="text-brand hover:text-brand-dark"
              >
                Log In
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      hideClose={successMessage !== ''}
    >
      {renderContent()}
    </Modal>
  );
}; // End of SignupModal component

export default SignupModal;
